// /opt/isp_erp/frontend/src/components/tickets/TecnicoPermisos.jsx

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Chip,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton,
  Grid,
  Paper
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import {
  Add as AddIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  EventNote as EventNoteIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import axios from '../../api/axios';

export default function TecnicoPermisos({ open, onClose, tecnicoId, tecnicoNombre }) {
  const [permisos, setPermisos] = useState([]);
  const [enPermisoHoy, setEnPermisoHoy] = useState(false);
  const [permisoActivo, setPermisoActivo] = useState(null);
  const [fechaInicio, setFechaInicio] = useState(null);
  const [fechaFin, setFechaFin] = useState(null);
  const [horaInicio, setHoraInicio] = useState(null);
  const [horaFin, setHoraFin] = useState(null);
  const [motivo, setMotivo] = useState('');
  const [loading, setLoading] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // ✅ Estados para editar
  const [editando, setEditando] = useState(null);
  const [editMode, setEditMode] = useState(false);

  useEffect(() => {
    if (open && tecnicoId) {
      cargarPermisos();
    }
  }, [open, tecnicoId]);

  const cargarPermisos = async () => {
    setCargando(true);
    setError('');
    try {
      const response = await axios.get(`/tickets/tecnicos/${tecnicoId}/permisos`);
      setPermisos(response.data.permisos || []);
      setEnPermisoHoy(response.data.en_permiso_hoy || false);
      setPermisoActivo(response.data.permiso_activo || null);
    } catch (err) {
      console.error('Error cargando permisos:', err);
      setError('Error al cargar permisos');
    } finally {
      setCargando(false);
    }
  };

  // ✅ Función para formatear fecha en YYYY-MM-DD sin zona horaria (para enviar al backend)
  const formatDateLocal = (date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // ✅ Función para formatear fecha desde el backend (YYYY-MM-DD → DD/MM/YYYY)
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    // ✅ Parsear manualmente sin zona horaria
    const [year, month, day] = dateStr.split('-').map(Number);
    return `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;
  };

  const formatHora = (horaStr) => {
    if (!horaStr) return '';
    return horaStr;
  };

  const handleCrearPermiso = async () => {
    if (!fechaInicio || !fechaFin) {
      setError('Debe seleccionar fechas de inicio y fin');
      return;
    }

    if (fechaFin < fechaInicio) {
      setError('La fecha fin debe ser mayor o igual a la fecha inicio');
      return;
    }

    if (horaInicio && horaFin && horaFin <= horaInicio) {
      setError('La hora fin debe ser mayor que la hora inicio');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const payload = {
        tecnico_id: tecnicoId,
        fecha_inicio: formatDateLocal(fechaInicio),
        fecha_fin: formatDateLocal(fechaFin),
        motivo: motivo || null
      };

      if (horaInicio) {
        payload.hora_inicio = horaInicio.toTimeString().slice(0, 5);
      }
      if (horaFin) {
        payload.hora_fin = horaFin.toTimeString().slice(0, 5);
      }

      console.log('📤 Payload enviado:', payload);

      await axios.post('/tickets/tecnicos/permisos', payload);

      setSuccess('✅ Permiso creado correctamente');
      setFechaInicio(null);
      setFechaFin(null);
      setHoraInicio(null);
      setHoraFin(null);
      setMotivo('');
      cargarPermisos();

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('❌ Error al crear permiso:', err.response?.data);
      setError(err.response?.data?.detail || 'Error al crear permiso');
    } finally {
      setLoading(false);
    }
  };

  const handleActualizarEstado = async (permisoId, estado) => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await axios.put(`/tickets/tecnicos/permisos/${permisoId}`, { estado });
      setSuccess(`✅ Permiso ${estado === 'aprobado' ? 'aprobado' : 'rechazado'} correctamente`);
      cargarPermisos();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('❌ Error al actualizar permiso:', err.response?.data);
      setError(err.response?.data?.detail || 'Error al actualizar permiso');
    } finally {
      setLoading(false);
    }
  };

  // ✅ Eliminar permiso (para pendiente y aprobado)
  const handleEliminarPermiso = async (permisoId) => {
    if (!window.confirm('¿Estás seguro de eliminar este permiso?')) return;

    setLoading(true);
    try {
      await axios.delete(`/tickets/tecnicos/permisos/${permisoId}`);
      setSuccess('✅ Permiso eliminado correctamente');
      cargarPermisos();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('❌ Error al eliminar permiso:', err.response?.data);
      setError(err.response?.data?.detail || 'Error al eliminar permiso');
    } finally {
      setLoading(false);
    }
  };

  // ✅ Editar permiso (para pendiente y aprobado)
  const handleEditarPermiso = (permiso) => {
    const fechaInicioEdit = new Date(permiso.fecha_inicio);
    const fechaFinEdit = new Date(permiso.fecha_fin);

    let horaInicioEdit = null;
    let horaFinEdit = null;
    if (permiso.hora_inicio) {
      const [h, m] = permiso.hora_inicio.split(':').map(Number);
      horaInicioEdit = new Date();
      horaInicioEdit.setHours(h, m, 0, 0);
    }
    if (permiso.hora_fin) {
      const [h, m] = permiso.hora_fin.split(':').map(Number);
      horaFinEdit = new Date();
      horaFinEdit.setHours(h, m, 0, 0);
    }

    setEditando({
      id: permiso.id,
      fecha_inicio: fechaInicioEdit,
      fecha_fin: fechaFinEdit,
      hora_inicio: horaInicioEdit,
      hora_fin: horaFinEdit,
      motivo: permiso.motivo || ''
    });
    setEditMode(true);
  };

  const handleGuardarEdicion = async () => {
    if (!editando.fecha_inicio || !editando.fecha_fin) {
      setError('Debe seleccionar fechas de inicio y fin');
      return;
    }

    if (editando.fecha_fin < editando.fecha_inicio) {
      setError('La fecha fin debe ser mayor o igual a la fecha inicio');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        tecnico_id: tecnicoId,
        fecha_inicio: formatDateLocal(editando.fecha_inicio),
        fecha_fin: formatDateLocal(editando.fecha_fin),
        motivo: editando.motivo || null
      };

      if (editando.hora_inicio) {
        payload.hora_inicio = editando.hora_inicio.toTimeString().slice(0, 5);
      }
      if (editando.hora_fin) {
        payload.hora_fin = editando.hora_fin.toTimeString().slice(0, 5);
      }

      console.log('📤 Payload edición enviado:', payload);

      await axios.put(`/tickets/tecnicos/permisos/${editando.id}/editar`, payload);

      setSuccess('✅ Permiso actualizado correctamente');
      setEditando(null);
      setEditMode(false);
      cargarPermisos();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('❌ Error al actualizar permiso:', err.response?.data);
      setError(err.response?.data?.detail || 'Error al actualizar permiso');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelarEdicion = () => {
    setEditando(null);
    setEditMode(false);
  };

  const getEstadoChip = (estado) => {
    const configs = {
      pendiente: { color: 'warning', label: '⏳ Pendiente' },
      aprobado: { color: 'success', label: '✅ Aprobado' },
      rechazado: { color: 'error', label: '❌ Rechazado' },
      completado: { color: 'info', label: '📌 Completado' }
    };
    const config = configs[estado] || { color: 'default', label: estado };
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EventNoteIcon color="primary" />
          <Typography variant="h6">Gestión de Permisos</Typography>
          {tecnicoNombre && (
            <Chip label={tecnicoNombre} size="small" color="primary" variant="outlined" />
          )}
          {enPermisoHoy && (
            <Chip label="🔴 EN PERMISO HOY" size="small" color="error" />
          )}
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
              {success}
            </Alert>
          )}

          {permisoActivo && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>📅 Permiso activo:</strong> {formatDate(permisoActivo.fecha_inicio)} → {formatDate(permisoActivo.fecha_fin)}
              {permisoActivo.hora_inicio && <span> {permisoActivo.hora_inicio}</span>}
              {permisoActivo.hora_fin && <span> - {permisoActivo.hora_fin}</span>}
              {permisoActivo.motivo && <span> - Motivo: {permisoActivo.motivo}</span>}
            </Alert>
          )}

          {/* Formulario para nuevo permiso con horas */}
          <Paper sx={{ p: 2, mb: 3, bgcolor: '#f8f9fa' }}>
            <Typography variant="subtitle2" gutterBottom>Nuevo Permiso</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                  <DatePicker
                    label="Fecha Inicio"
                    value={fechaInicio}
                    onChange={setFechaInicio}
                    minDate={new Date()}
                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} sm={2}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                  <TimePicker
                    label="Hora Inicio"
                    value={horaInicio}
                    onChange={setHoraInicio}
                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} sm={3}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                  <DatePicker
                    label="Fecha Fin"
                    value={fechaFin}
                    onChange={setFechaFin}
                    minDate={fechaInicio || new Date()}
                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} sm={2}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                  <TimePicker
                    label="Hora Fin"
                    value={horaFin}
                    onChange={setHoraFin}
                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Motivo"
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                  placeholder="Motivo"
                />
              </Grid>
              <Grid item xs={12} sm={1}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleCrearPermiso}
                  disabled={loading || !fechaInicio || !fechaFin}
                  sx={{ height: '100%', minHeight: '40px' }}
                >
                  {loading ? <CircularProgress size={24} /> : <AddIcon />}
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Lista de permisos */}
          <Typography variant="subtitle2" gutterBottom>
            Historial de Permisos
            <Chip label={permisos.length} size="small" sx={{ ml: 1 }} />
          </Typography>

          {cargando ? (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <CircularProgress size={24} />
              <Typography variant="caption" display="block">Cargando...</Typography>
            </Box>
          ) : permisos.length === 0 ? (
            <Alert severity="info">No hay permisos registrados para este técnico.</Alert>
          ) : (
            <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
              {permisos.map((p, index) => {
                const esPendiente = p.estado === 'pendiente';
                const esAprobado = p.estado === 'aprobado';
                const estaEditando = editando?.id === p.id;
                const esEditable = esPendiente || esAprobado;

                return (
                  <React.Fragment key={p.id}>
                    <ListItem>
                      {estaEditando ? (
                        <Box sx={{ width: '100%' }}>
                          <Grid container spacing={1}>
                            <Grid item xs={12} sm={3}>
                              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                                <DatePicker
                                  label="Fecha Inicio"
                                  value={editando.fecha_inicio}
                                  onChange={(val) => setEditando({ ...editando, fecha_inicio: val })}
                                  minDate={new Date()}
                                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                />
                              </LocalizationProvider>
                            </Grid>
                            <Grid item xs={12} sm={2}>
                              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                                <TimePicker
                                  label="Hora Inicio"
                                  value={editando.hora_inicio}
                                  onChange={(val) => setEditando({ ...editando, hora_inicio: val })}
                                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                />
                              </LocalizationProvider>
                            </Grid>
                            <Grid item xs={12} sm={3}>
                              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                                <DatePicker
                                  label="Fecha Fin"
                                  value={editando.fecha_fin}
                                  onChange={(val) => setEditando({ ...editando, fecha_fin: val })}
                                  minDate={editando.fecha_inicio || new Date()}
                                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                />
                              </LocalizationProvider>
                            </Grid>
                            <Grid item xs={12} sm={2}>
                              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                                <TimePicker
                                  label="Hora Fin"
                                  value={editando.hora_fin}
                                  onChange={(val) => setEditando({ ...editando, hora_fin: val })}
                                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                />
                              </LocalizationProvider>
                            </Grid>
                            <Grid item xs={12} sm={2}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Motivo"
                                value={editando.motivo}
                                onChange={(e) => setEditando({ ...editando, motivo: e.target.value })}
                              />
                            </Grid>
                            <Grid item xs={12} sm={1}>
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <IconButton
                                  size="small"
                                  color="success"
                                  onClick={handleGuardarEdicion}
                                  disabled={loading}
                                >
                                  <SaveIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={handleCancelarEdicion}
                                  disabled={loading}
                                >
                                  <CancelIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </Grid>
                          </Grid>
                        </Box>
                      ) : (
                        <>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography variant="body2">
                                  {formatDate(p.fecha_inicio)}
                                  {p.hora_inicio && <span> {formatHora(p.hora_inicio)}</span>}
                                  {' → '}
                                  {formatDate(p.fecha_fin)}
                                  {p.hora_fin && <span> {formatHora(p.hora_fin)}</span>}
                                </Typography>
                                {getEstadoChip(p.estado)}
                                {p.motivo && (
                                  <Chip label={p.motivo} size="small" variant="outlined" />
                                )}
                              </Box>
                            }
                            secondary={
                              <Typography variant="caption" color="text.secondary">
                                Creado: {new Date(p.creado_en).toLocaleString()}
                              </Typography>
                            }
                          />
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {esPendiente && (
                              <>
                                <IconButton
                                  size="small"
                                  color="success"
                                  onClick={() => handleActualizarEstado(p.id, 'aprobado')}
                                  title="Aprobar"
                                  disabled={loading}
                                >
                                  <CheckIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleActualizarEstado(p.id, 'rechazado')}
                                  title="Rechazar"
                                  disabled={loading}
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              </>
                            )}
                            {esEditable && (
                              <>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleEditarPermiso(p)}
                                  title="Editar permiso"
                                  disabled={loading}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleEliminarPermiso(p.id)}
                                  title="Eliminar permiso"
                                  disabled={loading}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </>
                            )}
                          </Box>
                        </>
                      )}
                    </ListItem>
                    {index < permisos.length - 1 && <Divider />}
                  </React.Fragment>
                );
              })}
            </List>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cerrar
        </Button>
        <Button
          variant="outlined"
          onClick={() => {
            cargarPermisos();
            setSuccess('🔄 Lista actualizada');
            setTimeout(() => setSuccess(''), 2000);
          }}
          disabled={loading}
        >
          🔄 Actualizar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
